import { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { detectSkinDisease } from "../../redux/slices/patientSlice";
import Sidebar from "../../components/Sidebar";
import Navbar from "../../components/Navbar";
import { useNavigate } from "react-router-dom";
import Loader from "../Loader";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Webcam from "react-webcam";

const Dashboard = () => {
  const dispatch   = useDispatch();
  const navigate   = useNavigate();
  const webcamRef  = useRef(null);
  const resultRef  = useRef(null);

  const { diseaseResult, loading } = useSelector((state) => state.patient);
  const [formData, setFormData] = useState({ profilePic: null });
  const [showCamera, setShowCamera] = useState(false);

  useEffect(() => {
    if (diseaseResult && resultRef.current) {
      resultRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [diseaseResult]);

  const handleFileChange = (e) => {
    setFormData({ ...formData, profilePic: e.target.files[0] });
    setShowCamera(false);
  };

  const capture = () => {
    const imageSrc = webcamRef.current.getScreenshot();
    fetch(imageSrc)
      .then((res) => res.blob())
      .then((blob) => {
        const file = new File([blob], "captured_image.jpg", { type: "image/jpeg" });
        setFormData({ ...formData, profilePic: file });
        setShowCamera(false);
      });
  };

  const handleSubmit = async () => {
    if (!formData.profilePic) {
      toast.error("Please select or capture an image.");
      return;
    }

    const data = new FormData();
    data.append("image", formData.profilePic);

    try {
      toast.info("⏳ Uploading image, please wait...");
      await dispatch(detectSkinDisease(data)).unwrap();
      toast.success("✅ Prediction Successful!");
    } catch (err) {
      console.error("Prediction Error:", err);
      toast.error("🚫 ML server sleeping or error occurred. Please try again!");
    }
  };

  return (
    <div className="relative">
      {loading && (
        <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <Loader />
        </div>
      )}

      <div className="flex flex-col lg:flex-row bg-[#F3F4F6] font-semibold min-h-screen relative">
        <ToastContainer position="top-right" autoClose={3000} />

        <Sidebar />
        <div className="flex-1 p-4 sm:p-6">
          <Navbar />

          <div className="flex flex-col lg:flex-row gap-10">
            {/* ───────────────── Upload Section ───────────────── */}
            <div className="bg-white shadow-xl mt-4 rounded-2xl p-6 w-full lg:max-w-3xl">
              <h2 className="text-2xl font-bold text-gray-800 mb-4 text-center">
                Upload or Capture Skin Image for Disease Detection
              </h2>

              <div className="flex flex-col items-center sm:flex-row sm:items-start gap-4">
                <div className="w-full">
                  <div className="flex flex-col sm:flex-row items-center gap-4">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="border border-gray-300 rounded-lg px-3 py-2 w-full sm:w-auto focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                      onClick={() => setShowCamera(!showCamera)}
                      className="bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded-lg"
                    >
                      {showCamera ? "Cancel Camera" : "Use Camera"}
                    </button>
                    <button
                      onClick={handleSubmit}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg shadow-md transition-all w-full sm:w-auto"
                    >
                      {loading ? "Processing..." : "Get Result"}
                    </button>
                  </div>

                  {showCamera && (
                    <div className="mt-4 flex flex-col items-center">
                      <Webcam
                        audio={false}
                        ref={webcamRef}
                        screenshotFormat="image/jpeg"
                        videoConstraints={{ facingMode: "environment" }}
                        className="rounded-lg w-full max-w-md"
                      />
                      <button
                        onClick={capture}
                        className="mt-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg"
                      >
                        Capture Image
                      </button>
                    </div>
                  )}

                  {formData.profilePic && (
                    <img
                      src={
                        formData.profilePic instanceof File
                          ? URL.createObjectURL(formData.profilePic)
                          : formData.profilePic
                      }
                      alt="Uploaded preview"
                      className="w-full max-w-md mt-4 mx-auto rounded-md"
                    />
                  )}
                </div>
              </div>

              {/* ─────────────── Results ─────────────── */}
              {diseaseResult && (
                <div
                  ref={resultRef}
                  className="mt-6 p-4 border-2 border-blue-500 rounded-lg bg-blue-50"
                >
                  <h3 className="text-2xl font-bold text-center mb-2">Detection Result</h3>

                  <div className="flex flex-col sm:flex-row items-center justify-center gap-2 mb-2">
                    <span className="text-lg font-semibold">Detected Disease:</span>
                    <span className="text-xl font-bold text-red-600">
                      {diseaseResult.detection.detectedDisease}
                    </span>
                  </div>

                  <div className="flex flex-col sm:flex-row items-center justify-center gap-2 mb-4">
                    <span className="text-md font-semibold">Confidence:</span>
                    <span className="text-md font-bold text-blue-600">
                      {diseaseResult.detection.confidence || "Unknown"}
                    </span>
                  </div>

                  {/* ────── Gemini Medicine Advice ────── */}
                  {diseaseResult.detection.medicines?.length > 0 ? (
                    <div className="mt-4">
                      <h4 className="text-xl font-bold text-center mb-2">
                        💊 Medicine Recommendations
                      </h4>
                      <div className="space-y-4">
                        {diseaseResult.detection.medicines.map((med, idx) => (
                          <div
                            key={idx}
                            className="border rounded-lg p-4 bg-white shadow-md"
                          >
                            <p><strong>Name:</strong> {med.name}</p>
                            <p><strong>Dosage:</strong> {med.dosage}</p>
                            <p><strong>Usage:</strong> {med.usage}</p>
                            <p className="text-red-600">
                              <strong>Precautions:</strong> {med.precautions}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <p className="mt-4 text-center text-gray-500">
                      ⚠️ No AI-generated medicine suggestions available.
                    </p>
                  )}

                  <p className="text-gray-700 mt-4 text-center">
                    This result is generated using AI prediction. Please consult a licensed dermatologist for a final diagnosis.
                  </p>

                  <div className="flex justify-center mt-4">
                    <button
                      onClick={() => navigate("/find-dermatologist")}
                      className="bg-green-500 hover:bg-green-600 text-white px-6 py-2 rounded-lg shadow-md flex items-center"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 mr-2"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Find Dermatologist
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* ─────────── Instructions Sidebar ─────────── */}
            <div className="mt-4 lg:mt-6 lg:border-l-2 mb-32 border-gray-400 px-4 lg:px-10">
              <h2 className="text-xl font-bold underline mb-4">
                Instructions for uploading the Image
              </h2>
              <ul className="list-disc text-sm sm:text-base space-y-2 pl-5">
                <li>Ensure the image is clear and well‑lit.</li>
                <li>Only upload JPG, PNG, or JPEG files.</li>
                <li>Avoid blurry or low‑resolution images.</li>
                <li>Make sure the affected skin area is fully visible.</li>
                <li>Click “Get Result” after selecting or capturing an image.</li>
                <li>Image size must be ≤ 5 MB.</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
